import { JUNGLE_BACKEND_PROD_DOMAIN } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getAnonymousUploadClaim } from "./anonymous-upload-claims";
import {
  buildLocalUploadPageUrl,
  createAndWaitForFileProcessing,
  FileUploadError,
  FileUploadErrorCode,
  FileUploadStage,
  requestFileProcessingTask,
} from "./file-upload-client";

export const LANDING_FILE_PROCESSING_URL = `${JUNGLE_BACKEND_PROD_DOMAIN}/file_or_url/create_task_to_get_file_data`;

/** Retry only a missing task, once, using its original identity and already-uploaded bytes. */
export async function resumeLandingFileProcessing<T>({
  taskId,
  waitForTask,
  getIdToken,
  cancellationSignal,
  onRecovered,
}: {
  taskId: string;
  waitForTask: (taskId: string) => Promise<T>;
  getIdToken: () => Promise<string | null>;
  cancellationSignal?: AbortSignal;
  onRecovered?: () => void;
}): Promise<T> {
  try {
    return await waitForTask(taskId);
  } catch (error) {
    // Only an absent processing record can be recreated; processing failures retain their original outcome.
    const isMissingTask = error instanceof FileUploadError && error.code === FileUploadErrorCode.MissingTask;
    if (!isMissingTask) throw error;
    const claim = getAnonymousUploadClaim(taskId, LANDING_FILE_PROCESSING_URL);
    const canReuseSavedSource = claim !== undefined && !cancellationSignal?.aborted;
    if (!canReuseSavedSource) throw error;
    const idToken = await getIdToken();
    const result = await createAndWaitForFileProcessing({
      requestedTaskId: taskId,
      requestTask: async () => {
        const returnedTaskId = await requestFileProcessingTask({
          url: LANDING_FILE_PROCESSING_URL,
          requestedTaskId: taskId,
          body: {
            ...claim.body,
            page_url: buildLocalUploadPageUrl(taskId, claim.body.uploaded_file_s3_object_key),
            file_processing_purpose: "new_upload",
          },
          idToken,
          cancellationSignal,
        });
        if (returnedTaskId !== taskId) {
          throw new FileUploadError({
            message: "The recovery response did not match the requested task",
            stage: FileUploadStage.CreateProcessingTask,
            code: FileUploadErrorCode.InvalidResponse,
            retryable: false,
          });
        }
        return taskId;
      },
      waitForTask,
    });
    // Diagnostics cannot invalidate a recovered source.
    try {
      onRecovered?.();
    } catch {
      /* best effort */
    }
    return result;
  }
}
